import { NgClass, NgStyle } from "@angular/common";
import { Component, Input, OnDestroy, inject } from "@angular/core";
import { ForgeButtonModule, ForgeCircularProgressModule, ForgeIconButtonModule, ForgeTooltipModule } from "@tylertech/forge-angular";
import { Utilities } from "@tylertech/shared-module";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { Action, Command } from "../../interfaces";
import { CommandService } from "../../services";
import { TooltipPlacement } from "@tylertech/forge";

@Component({
    selector: "command",
    templateUrl: "./command.tmpl.html",
    styleUrl: "./command.component.scss",
    imports: [
        NgClass,
        NgStyle,
        ForgeIconButtonModule,
        ForgeTooltipModule,
        ForgeButtonModule,
        ForgeCircularProgressModule,
    ],
})
export class CommandComponent implements Command, OnDestroy
{
    private readonly $commandService = inject(CommandService);

    @Input() readonly command: Command;

    /**
     * Only used for Icon commands.
     *
     * @deprecated - Should be replaced with a property that tests if the `type` field contains the 'dense' keyword.
     */
    @Input() readonly dense: boolean;
    @Input() useMouseDown: boolean;

    private readonly destructor$ = new Subject<void>();
    compactMode: boolean;

    constructor()
    {
        this.$commandService.compactMode
            .pipe(takeUntil(this.destructor$))
            .subscribe((compact: boolean) =>
            {
                this.compactMode = compact;
            });
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    get icon(): string
    {
        return this.command.icon;
    }

    get color(): string
    {
        return this.command.color;
    }

    get hasLabel(): boolean
    {
        return !!this.label;
    }

    get ariaLabel(): string
    {
        return this.hasLabel ? this.command.label : this.command.tooltip;
    }

    get label(): string
    {
        if (this.isWaiting)
            return "";
        return this.command.label;
    }

    get tooltip(): string
    {
        return this.command.tooltip;
    }

    get action(): Action
    {
        return this.command.action;
    }

    get isEnabled(): boolean
    {
        return this.command.isEnabled;
    }

    get isVisible(): boolean
    {
        return this.command.isVisible;
    }

    get isWaiting(): boolean
    {
        return this.command["isWaiting"];
    }

    get tooltipDirection(): TooltipPlacement
    {
        return this.command.tooltipDirection ?? "bottom";
    }

    get disabledTooltip(): string
    {
        return this.command.disabledTooltip;
    }

    get class(): string
    {
        return this.command.classString;
    }

    get theme(): string
    {
        return this.isEnabled ? this.color : "";
    }

    get iconColor(): string
    {
        let color = "";
        if (!this.isEnabled || Utilities.IsNullOrUndefinedOrEmpty(this.color))
        {
            color = "text-medium";
        }
        else
        {
            color = this.color;
        }

        return `var(--forge-theme-${color})`;
    }

    mouseDown($event: MouseEvent): void
    {
        if (this.useMouseDown)
            this.action($event);
    }

    click($event: PointerEvent): void
    {
        const isKeyboard = $event.detail === 0 || $event.pointerType === ""; //keyboard Enter/Space (not mouse click)
        if (!this.useMouseDown || isKeyboard)
            this.action($event);
    }
}
